import { yupResolver } from "@hookform/resolvers/yup"
import {
  Button,
  HoverCard,
  LoadingOverlay,
  Modal,
  Select,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core"
import {
  IconDeviceFloppy,
  IconEdit,
  IconHomePlus,
  IconTrash,
} from "@tabler/icons-react"
import { debounce } from "lodash"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import * as yup from "yup"
import { CollectionQuery } from "../../../models/collection.model"
import { Property } from "../../../models/property.model"
import { Unit } from "../../../models/unit.model"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import EmptyIcon from "../../../shared/component/empty-icon/empty-icon"
import { Pagination } from "../../../shared/component/pagination/pagination"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import {
  useAddUnitMutation,
  useLazyGetUnitsOfPropertyQuery,
  useRemoveUnitMutation,
  useToggleUnitStatusMutation,
  useUpdateUnitMutation,
} from "../store/property.query"
import { motion } from "framer-motion"
import { Edit, Search, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"

type NewUnitComponentProps = {
  property: any
  open: any
  opened: boolean
  close: any
}
export function NewUnitComponent(props: NewUnitComponentProps) {
  const params = useParams()
  const { t } = useTranslation()
  let { property, open, opened, close } = props
  const [mode, setMode] = useState<"new" | "edit">("new")
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null)
  const [mUnit, setMUnit] = useState<Unit | null | undefined>(null)
  const [unitsCollection, setUnitsCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
    filter: [
      [{ field: "propertyId", value: property?.data?.id, operator: "=" }],
    ],
  })
  const [addUnits, addedUnits] = useAddUnitMutation()
  const [updateUnit, updatedUnit] = useUpdateUnitMutation()
  const [removeUnits, removedUnits] = useRemoveUnitMutation()
  // const [getProperties, properties] = useLazyGetPropertiesOfUnitQuery();
  const [getUnits, units] = useLazyGetUnitsOfPropertyQuery()
  const [toggleUnitStatus, toggleUnitStatusResponse] =
    useToggleUnitStatusMutation()
  const defaultValue: Unit = {
    name: "",
    propertyId: "",
    type: "",
    floor: "",
    description: "",
  }

  let schema: any = yup.object().shape({
    name: yup
      .string()
      .min(2, t("nameMin", { count: 2 }))
      .max(100, t("nameMax", { count: 100 }))
      .required(t("nameRequired")),
    type: yup.string().typeError(t("typeString")).required(t("typeRequired")),
    unitArea: yup
      .string()
      .typeError(t("typeValidation", { field: t("area"), type: t("string") }))
      .required(t("requiredValidation", { field: t("area") })),
    description: yup
      .string()
      .nullable()
      .typeError("Description should be a text."),
    // .required("Description is required."),
    // propertyId: yup.string().typeError("Property should be a text").required("Property is required"),
  })
  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<Unit>({
    resolver: yupResolver(schema),
    mode: "all",
  })

  useEffect(() => {
    getUnits({
      ...unitsCollection,
      filter: [
        [{ field: "propertyId", value: property?.data?.id, operator: "=" }],
      ],
      orderBy: [{ field: "createdAt", direction: "desc" }],
    })
  }, [unitsCollection, getUnits, params, property])

  function onSubmit(data: any) {
    try {
      data.propertyId = params?.id as string

      if (mode == "new") {
        addUnits(data).then((response: any) => {
          // if (!response?.error) {
          reset(defaultValue)
          close()
          // }
        })
      } else {
        updateUnit({ ...data, id: selectedUnit as string }).then(
          (response: any) => {
            // if (!response?.error) {
            reset(defaultValue)
            close()
            // }
          },
        )
      }
    } catch (e: any) {
      console.log("🚀 ~ onSubmit ~ e:", e)
    }
  }
  const onError = (error: any) => {
    console.log("🚀 ~ onError ~ error:", error)
  }

  function openUpdateModal(unit: Unit) {
    open()
    setSelectedUnit(unit?.id as string)
    setValue("name", unit.name as string)
    setValue("type", unit.type as string)
    setValue("unitArea", unit.unitArea as string)
    setValue("floor", unit.floor as string)
    setValue("description", unit.description as string)
    setMode("edit")
    setMUnit(unit)
  }

  return (
    <>
      <div className="bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl p-6 border border-gray-700 mb-8  overflow-x-scroll">
        <LoadingOverlay visible={units?.isLoading || units?.isFetching} />

        <div className="flex w-full justify-end gap-2 mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder={t("searchHere")}
              className="bg-gray-700 text-white placeholder-gray-400 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyUp={debounce(
                (event: any) =>
                  setUnitsCollection({
                    ...unitsCollection,
                    search: event.target.value,
                    searchFrom: ["name"],
                  }),
                1000,
              )}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={18}
            />
          </div>
          <Button
            variant="filled"
            loading={addedUnits?.isLoading}
            className={`bg-primary text-white flex space-x-2 items-center`}
            type="button"
            onClick={() => {
              open()
              setMode("new")
            }}
            classNames={{ label: "flex space-x-1" }}
            size={"xs"}
          >
            <IconHomePlus size={20} strokeWidth={1.5} /> {t("addUnit")}
          </Button>
        </div>

        <table className="min-w-full divide-y divide-gray-700">
          <thead
          // className="text-xs text-gray-700 capitalize bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
          >
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("name")}
              </th>

              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("area")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("floor")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("type")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("occupied")}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("status")}
              </th>
              {/* <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("description")}
              </th> */}

              {/* <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
              >
                {t("registeredAt")}
              </th> */}
              <th className="className='px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider'">
                {t("action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {units?.data?.data &&
              units?.data?.data.length > 0 &&
              units?.data?.data.map((unit: Unit, idx: number) => (
                <motion.tr
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border-b dark:bg-gray-800 text-xs dark:border-gray-700"
                >
                  <th className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.name}
                  </th>
                  <th className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.unitArea}
                  </th>
                  <th className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.floor}
                  </th>
                  {/* <td className="py-4 px-6 text-xs">{document?.referenceNumber}</td> */}

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.occupied ? (
                      <span className="bg-red-100 text-red-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
                        {t("occupied")}
                      </span>
                    ) : (
                      <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300">
                        {t("available")}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.enabled ? t("active") : t("inactive")}
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                   
                    <HoverCard width={280} shadow="md">
                      <HoverCard.Target>
                        <Text size="sm" className={"cursor-pointer"}>
                          {unit.description && unit.description.length > 15
                            ? unit?.description?.substring(0, 12) + "..."
                            : unit?.description}
                        </Text>
                      </HoverCard.Target>
                      <HoverCard.Dropdown
                        className={
                          "text-justify break-all wrap max-h-60 overflow-auto"
                        }
                      >
                        <Text size="sm">{unit?.description}</Text>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  </td> */}
                  {/* 
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    {unit?.createdAt ? (
                      <span>{dateFormat(unit?.createdAt)}</span>
                    ) : (
                      <span>-</span>
                    )}
                  </td> */}

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100 ">
                    <div className="flex space-x-2">
                      {/* <Button
                                                variant="filled"
                                                // loading={removedUnits.isLoading && removedUnits?.originalArgs === unit?.id}
                                                className={`bg-primary text-white flex space-x-2 items-center`}
                                                type="button"
                                                onClick={() => openUpdateModal(unit)}
                                                classNames={{ label: "flex space-x-1" }}
                                                size={"xs"}
                                            >
                                                <IconEdit size={20} strokeWidth={1.5} />
                                            </Button> */}

                      <button
                        // loading={removedUnits.isLoading && removedUnits?.originalArgs === unit?.id}
                        className="text-indigo-400 hover:text-indigo-300 mr-2"
                        onClick={() => openUpdateModal(unit)}
                      >
                        <Edit size={18} />
                      </button>

                      <Confirmation
                        type={"danger"}
                        message={t("permanentDeleteMessage")}
                        onYes={() => {
                          removeUnits(unit?.id as string)
                          close()
                        }}
                        header={t("permanentDeleteTitle")}
                      >
                        {/* <Button
                                                    variant="filled"
                                                    loading={removedUnits.isLoading && removedUnits?.originalArgs === unit?.id}
                                                    className={`bg-danger text-white flex space-x-2 items-center`}
                                                    type="button"
                                                    classNames={{ label: "flex space-x-1" }}
                                                    size={"xs"}
                                                >
                                                    <IconTrash size={20} strokeWidth={1.5} />
                                                </Button> */}
                        <button
                          // loading={removedUnits.isLoading && removedUnits?.originalArgs === unit?.id}
                          className="text-red-400 hover:text-red-300 mr-2 mt-2"
                        >
                          <Trash2 size={18} />
                        </button>
                      </Confirmation>
                    </div>
                  </td>
                </motion.tr>
              ))}
          </tbody>
        </table>
        {!(units?.data?.count as number) ? (
          <div className="w-full relative flex justify-center items-center h-56">
            <EmptyIcon />
          </div>
        ) : (
          <></>
        )}

        {units?.data && units?.data?.count > 0 && (
          <div className="w-full flex justify-end items-center mt-2 mb-2">
            <Pagination
              total={units?.data?.count as number}
              defaultPageSize={10}
              pageSize={[5, 10, 15, 20]}
              onPaginationChange={(skip: number, top: number) => {
                setUnitsCollection({
                  ...unitsCollection,
                  skip: skip,
                  top: top,
                  filter: [
                    [
                      {
                        field: "propertyId",
                        operator: "=",
                        value: property?.data?.id as string,
                      },
                    ],
                  ],
                })
              }}
            />
          </div>
        )}
      </div>

      <Modal
        opened={opened}
        onClose={close}
        size={"sm"}
        title={mode == "new" ? t("addUnit") : t("editUnit")}
        centered
      >
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <div className="flex w-full space-x-4 justify-between ">
            <div className="w-full">
              <TextInput
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                error={errors.name && `${errors?.name?.message}`}
                withAsterisk
                label={t("name")}
                placeholder={t("name")}
                {...register("name")}
              />
              <TextInput
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                error={errors.floor && `${errors?.floor?.message}`}
                withAsterisk
                label={t("floor")}
                placeholder={t("floor")}
                {...register("floor")}
              />
              <TextInput
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                error={errors.unitArea && `${errors?.unitArea?.message}`}
                withAsterisk
                label={t("area")}
                placeholder={t("area")}
                {...register("unitArea")}
              />
              <TextInput
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                error={errors.type && `${errors?.type?.message}`}
                withAsterisk
                label={t("type")}
                placeholder={t("type")}
                {...register("type")}
              />
              <Textarea
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                error={errors.description && `${errors?.description?.message}`}
                // withAsterisk
                label={t("description")}
                placeholder={t("description")}
                {...register("description")}
              />
            </div>
          </div>

          <div className="pt-8 flex justify-end gap-3">
            {mUnit && (
              <Confirmation
                type={"notify"}
                message={
                  <span>
                    {t("activateMessage", {
                      action: mUnit?.enabled ? t("deactivate") : t("activate"),
                    })}
                  </span>
                }
                onYes={() =>
                  toggleUnitStatus(`${selectedUnit}`).then((res) => close())
                }
                header={t("activateTitle", {
                  action: mUnit?.enabled ? t("deactivate") : t("activate"),
                })}
              >
                <Button
                  variant="filled"
                  loading={toggleUnitStatusResponse.isLoading}
                  className={`${
                    mUnit?.enabled ? " bg-danger" : "bg-green-500"
                  } text-white`}
                  type="button"
                  classNames={{ label: "flex space-x-1" }}
                  size={"xs"}
                >
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="fill-current h-6"
                      viewBox="0 0 48 48"
                    >
                      <path d="M23.5 5C17.16639 5 12 10.16639 12 16.5C12 18.130861 12.341389 19.689962 12.957031 21.099609 A 1.50015 1.50015 0 1 0 15.707031 19.900391C15.252673 18.860038 15 17.713139 15 16.5C15 11.78761 18.78761 8 23.5 8C28.21239 8 32 11.78761 32 16.5C32 17.08427 31.94138 17.652314 31.830078 18.201172 A 1.50015 1.50015 0 1 0 34.769531 18.798828C34.92023 18.055686 35 17.28573 35 16.5C35 10.16639 29.83361 5 23.5 5 z M 23.5 12C21.032499 12 19 14.032499 19 16.5L19 25.759766L18.138672 25.404297C14.483804 23.900444 10.334734 26.436466 10.005859 30.375 A 1.50015 1.50015 0 0 0 10.693359 31.765625L19.957031 37.667969C20.601036 38.078718 21.151526 38.620029 21.576172 39.255859L23.033203 41.441406 A 1.50015 1.50015 0 0 0 23.035156 41.441406C23.803886 42.591886 25.189849 43.186167 26.554688 42.945312L31.882812 42.005859C33.603893 41.703285 34.998876 40.422039 35.449219 38.734375 A 1.50015 1.50015 0 0 0 35.451172 38.726562L37.832031 29.576172C38.653863 26.49462 36.64066 23.318721 33.501953 22.748047L28 21.748047L28 16.5C28 14.032499 25.967501 12 23.5 12 z M 23.5 15C24.346499 15 25 15.653501 25 16.5L25 23 A 1.50015 1.50015 0 0 0 26.232422 24.476562L32.964844 25.699219C34.424137 25.964545 35.315668 27.370273 34.933594 28.802734 A 1.50015 1.50015 0 0 0 34.929688 28.8125L32.550781 37.960938C32.399124 38.529274 31.940201 38.949356 31.363281 39.050781 A 1.50015 1.50015 0 0 0 31.363281 39.052734L26.033203 39.992188C25.834042 40.027338 25.642567 39.944908 25.529297 39.775391L24.072266 37.591797C23.417016 36.610136 22.565912 35.77367 21.570312 35.138672 A 1.50015 1.50015 0 0 0 21.568359 35.138672L13.251953 29.835938C13.814585 28.352335 15.413607 27.528548 16.996094 28.179688L19.929688 29.386719 A 1.50015 1.50015 0 0 0 22 28L22 16.5C22 15.653501 22.653501 15 23.5 15 z" />
                    </svg>
                  </span>
                  {mUnit?.enabled ? (
                    <span>{t("deactivate")}</span>
                  ) : (
                    <span>{t("activate")}</span>
                  )}
                </Button>
              </Confirmation>
            )}

            <Button
              variant="filled"
              className="bg-primary text-white"
              type="submit"
              classNames={{ label: "flex space-x-1" }}
              size={"xs"}
              loading={addedUnits.isLoading || updatedUnit.isLoading}
              //  onClick={() => handleAssignDriver()}
            >
              <span>
                <IconDeviceFloppy size={22} strokeWidth={1.5} />
              </span>
              {t("submit")}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  )
}
